'use client';

import { useEffect, useRef, useState, useTransition } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Globe, ChevronDown, Loader2 } from 'lucide-react';
import {
  HELP_LANGUAGES,
  type HelpLocale,
} from './help-header-i18n';

interface HelpLanguageMenuProps {
  /** Currently active UI locale. */
  currentLocale: HelpLocale;
  /**
   * Map of `<locale>` → href the dropdown should navigate to. Must include
   * an entry for every locale we want the user to be able to switch to.
   * If a locale is missing, that flag is hidden from the menu.
   *
   * For the article page the parent computes per-language slugs from the
   * article's `translation_group` and falls back to `/{locale}/help` when
   * a specific translation doesn't exist. For the index page the parent
   * just provides `/{locale}/help` for each.
   */
  hrefByLocale: Partial<Record<HelpLocale, string>>;
  /** Localized "Language" label used as button title / aria-label. */
  label: string;
}

/**
 * Language dropdown for the help shell. Visual parity with the landing
 * navbar's language selector (`app/(landing)/sections/NavBar.tsx`):
 * bordered pill with the lang code + chevron as trigger, rounded-xl
 * dropdown, flag-and-name items, no checkmark icon (active item is
 * highlighted with purple background + bold text).
 *
 * Items are real `<Link>`s so navigation works even when JS is off —
 * only the open/close toggle needs `'use client'`.
 */
export function HelpLanguageMenu({ currentLocale, hrefByLocale, label }: HelpLanguageMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [cambiandoA, setCambiandoA] = useState<HelpLocale | null>(null);
  const [navegando, iniciarNavegacion] = useTransition();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  // Cambiar de idioma se sentía lento porque la página del otro idioma se
  // pedía recién al hacer clic. Se precarga en cuanto se monta la cabecera,
  // así el salto es casi inmediato.
  useEffect(() => {
    for (const href of Object.values(hrefByLocale)) {
      if (href) router.prefetch(href);
    }
  }, [hrefByLocale, router]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (!navegando && cambiandoA) {
      setCambiandoA(null);
      setIsOpen(false);
    }
  }, [navegando, cambiandoA]);

  const current = HELP_LANGUAGES.find((l) => l.code === currentLocale) ?? HELP_LANGUAGES[0];

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-purple-100 hover:border-purple-300 transition-colors cursor-pointer"
        title={`${label} · ${current.label}`}
        aria-label={`${label}: ${current.label}`}
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        <Globe className="w-4 h-4 text-purple-500" />
        <span className="font-medium">{currentLocale.toUpperCase()}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div
          className="absolute right-0 top-full mt-3 bg-white rounded-xl shadow-lg border border-gray-200 py-1 min-w-[120px] z-[200]"
          role="menu"
        >
          {HELP_LANGUAGES.map((lang) => {
            const href = hrefByLocale[lang.code];
            if (!href) return null;
            const isActive = lang.code === currentLocale;
            return (
              <Link
                key={lang.code}
                href={href}
                prefetch
                onClick={(e) => {
                  // Con JS, navegamos nosotros para poder avisar de que el
                  // cambio está en marcha; sin JS, el enlace funciona igual.
                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                  e.preventDefault();
                  if (isActive) { setIsOpen(false); return; }
                  setCambiandoA(lang.code);
                  iniciarNavegacion(() => router.push(href));
                }}
                className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2.5 hover:bg-purple-50 transition-colors cursor-pointer ${
                  isActive ? 'bg-purple-50 text-purple-600 font-semibold' : 'text-gray-700'
                } ${navegando && cambiandoA !== lang.code ? 'opacity-50' : ''}`}
                role="menuitem"
                aria-busy={cambiandoA === lang.code || undefined}
              >
                <span>{lang.flag}</span>
                <span>{lang.label}</span>
                {cambiandoA === lang.code && navegando && (
                  <Loader2 className="w-3.5 h-3.5 ml-auto animate-spin text-purple-500" />
                )}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
